import { Directive, ElementRef, HostBinding, HostListener, Input } from '@angular/core';

type DragScrollAxis = 'x' | 'y' | 'both';

@Directive({
  selector: '[dragToScroll]',
  standalone: true,
})
export class DragToScrollDirective {
  /**
   * Scroll axis to control while dragging.
   * - 'y' works great for vertical lists
   * - 'x' for horizontal carousels
   * - 'both' for large canvases
   */
  @Input() dragScrollAxis: DragScrollAxis = 'both';

  /**
   * Enables/disables drag-to-scroll behavior + "grab" cursor styling.
   * Useful for empty lists where the grab cursor feels wrong.
   */
  @Input()
  set dragScrollEnabled(value: boolean | string | null | undefined) {
    // Accept boolean bindings as well as attribute-style usage.
    this._dragScrollEnabled = value !== null && `${value}` !== 'false';

    // If we get disabled mid-gesture, immediately reset state.
    if (!this._dragScrollEnabled) {
      if (this.hasCapturedPointer && this.pointerId !== null) {
        try {
          this.host.releasePointerCapture(this.pointerId);
        } catch {
          // ignore
        }
      }

      if (this.previousScrollBehaviorInline !== null) {
        this.host.style.scrollBehavior = this.previousScrollBehaviorInline;
      } else {
        this.host.style.removeProperty('scroll-behavior');
      }
      this.previousScrollBehaviorInline = null;

      this.isPointerDown = false;
      this.isDragging = false;
      this.draggingClass = false;
      this.pointerId = null;
      this.hasCapturedPointer = false;
      this.suppressClickUntilMs = 0;
    }
  }
  get dragScrollEnabled(): boolean {
    return this._dragScrollEnabled;
  }

  /** Pixels before we treat the gesture as a drag (vs a click). */
  @Input() dragScrollThresholdPx = 6;

  @HostBinding('class.drag-scroll--enabled') get enabledClass(): boolean {
    return this.dragScrollEnabled;
  }
  @HostBinding('class.drag-scroll--dragging') draggingClass = false;

  private _dragScrollEnabled = true;
  private isPointerDown = false;
  private isDragging = false;
  private pointerId: number | null = null;
  private hasCapturedPointer = false;

  private startClientX = 0;
  private startClientY = 0;
  private startScrollLeft = 0;
  private startScrollTop = 0;

  /**
   * We suppress the click that may fire immediately after a drag ends.
   * Use a timestamp (not a boolean) so it auto-recovers even if the browser never fires a click.
   */
  private suppressClickUntilMs = 0;
  private previousScrollBehaviorInline: string | null = null;

  constructor(private el: ElementRef<HTMLElement>) {}

  private get host(): HTMLElement {
    return this.el.nativeElement;
  }

  private shouldIgnoreStart(target: EventTarget | null): boolean {
    const t = target as HTMLElement | null;
    if (!t) return false;
    // Don't hijack interactions with inputs/buttons/links/etc.
    return !!t.closest(
      [
        'a',
        'button',
        'input',
        'textarea',
        'select',
        'option',
        '[contenteditable="true"]',
        '[role="button"]',
        'mat-checkbox',
        '.mat-mdc-checkbox',
      ].join(',')
    );
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (!this.dragScrollEnabled) return;
    // Mouse primary button only; touch already supports swipe scrolling natively.
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (this.shouldIgnoreStart(event.target)) return;

    this.isPointerDown = true;
    this.isDragging = false;
    this.pointerId = event.pointerId;
    this.hasCapturedPointer = false;
    this.suppressClickUntilMs = 0;

    this.startClientX = event.clientX;
    this.startClientY = event.clientY;
    this.startScrollLeft = this.host.scrollLeft;
    this.startScrollTop = this.host.scrollTop;

    // Important: don't set pointer capture on pointerdown.
    // Doing so can cause the click to be dispatched on the container instead of the inner target,
    // breaking (click) handlers like selectThread(). We'll capture only once a drag is detected.
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.dragScrollEnabled) return;
    if (!this.isPointerDown) return;
    if (this.pointerId !== null && event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startClientX;
    const dy = event.clientY - this.startClientY;

    if (!this.isDragging) {
      const movedEnough =
        Math.abs(dx) >= this.dragScrollThresholdPx || Math.abs(dy) >= this.dragScrollThresholdPx;
      if (!movedEnough) return;

      this.isDragging = true;
      this.draggingClass = true;

      // Capture only once dragging begins (safe for child clicks).
      if (!this.hasCapturedPointer) {
        try {
          this.host.setPointerCapture(event.pointerId);
          this.hasCapturedPointer = true;
        } catch {
          // ignore
        }
      }

      // Smooth scrolling fights "grab to scroll" (jittery). Force immediate while dragging.
      this.previousScrollBehaviorInline = this.host.style.scrollBehavior || null;
      this.host.style.scrollBehavior = 'auto';
    }

    // Prevent text selection while dragging
    event.preventDefault();

    const axis = this.dragScrollAxis;
    if (axis === 'x' || axis === 'both') {
      // "Grab" behavior: move right => content follows cursor => scrollLeft decreases.
      this.host.scrollLeft = this.startScrollLeft - dx;
    }
    if (axis === 'y' || axis === 'both') {
      this.host.scrollTop = this.startScrollTop - dy;
    }
  }

  @HostListener('pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.dragScrollEnabled) return;
    if (this.pointerId !== null && event.pointerId !== this.pointerId) return;
    this.endPointer(event.pointerId);
  }

  @HostListener('pointercancel', ['$event'])
  onPointerCancel(event: PointerEvent): void {
    if (!this.dragScrollEnabled) return;
    if (this.pointerId !== null && event.pointerId !== this.pointerId) return;
    this.endPointer(event.pointerId);
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (Date.now() > this.suppressClickUntilMs) return;
    // Suppress the click that may fire right after a drag.
    event.preventDefault();
    event.stopPropagation();
    this.suppressClickUntilMs = 0;
  }

  private endPointer(pointerId: number): void {
    this.isPointerDown = false;

    if (this.isDragging) {
      this.isDragging = false;
      this.draggingClass = false;
      // Block the post-drag click (if any) without breaking future clicks.
      this.suppressClickUntilMs = Date.now() + 300;
    } else {
      this.suppressClickUntilMs = 0;
    }

    if (this.hasCapturedPointer) {
      try {
        this.host.releasePointerCapture(pointerId);
      } catch {
        // ignore
      }
    }
    this.pointerId = null;
    this.hasCapturedPointer = false;

    if (this.previousScrollBehaviorInline !== null) {
      this.host.style.scrollBehavior = this.previousScrollBehaviorInline;
    } else {
      // Remove inline override if there wasn't one.
      this.host.style.removeProperty('scroll-behavior');
    }
    this.previousScrollBehaviorInline = null;
  }
}

